import React, { useState, useEffect, useRef } from 'react';

/**
 * Composant de sélection d'opération avec recherche rapide
 */
const OperationSelect = ({ 
  value, 
  onChange, 
  operations = [],
  placeholder = "Rechercher une opération...",
  className = "",
  required = false,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les opérations en fonction de la recherche
  const filteredOperations = operations.filter(op => 
    op.numero_operation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    op.statut?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    op.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gérer la sélection
  const handleSelect = (operation) => {
    onChange(operation.id);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Navigation au clavier
  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      e.preventDefault();
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOperations.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOperations.length) {
          handleSelect(filteredOperations[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Couleur du statut
  const getStatusColor = (statut) => {
    const colors = {
      'en cours': 'bg-blue-100 text-blue-700',
      'terminée': 'bg-green-100 text-green-700',
      'annulée': 'bg-red-100 text-red-700',
      'en attente': 'bg-yellow-100 text-yellow-700'
    };
    return colors[statut?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  // Afficher la valeur sélectionnée
  const selectedOp = operations.find(op => op.id === value);
  const displayValue = selectedOp 
    ? `${selectedOp.numero_operation} - ${selectedOp.statut}`
    : '';

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            )}
          </svg>
        </div>
      </div>

      {/* Dropdown avec résultats */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
          {filteredOperations.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              {searchTerm ? `Aucune opération trouvée pour "${searchTerm}"` : 'Aucune opération disponible'}
            </div>
          ) : (
            <div>
              {filteredOperations.map((op, index) => (
                <button
                  key={op.id}
                  type="button"
                  onClick={() => handleSelect(op)}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-green-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    index === highlightedIndex ? 'bg-green-100' : ''
                  } ${value === op.id ? 'bg-green-50 font-medium' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{op.numero_operation}</div>
                      {op.client_name && (
                        <div className="text-xs text-gray-500 mt-1">{op.client_name}</div>
                      )}
                      <div className="mt-1">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(op.statut)}`}>
                          {op.statut}
                        </span>
                      </div>
                    </div>
                    {value === op.id && (
                      <span className="text-green-600">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Aide visuelle */}
      {isOpen && filteredOperations.length > 0 && (
        <div className="mt-1 px-3 py-2 bg-green-50 border border-green-200 rounded-md text-xs text-green-700">
          💡 Recherche par numéro, statut ou client • ↑↓ pour naviguer • Entrée pour sélectionner
        </div>
      )}
    </div>
  );
};

export default OperationSelect;
